import styles from './GlobalCopilotOverlay.module.css'
import {ActionList, Button, Link, RelativeTime} from '@primer/react'
import {Blankslate, SkeletonAvatar, SkeletonText} from '@primer/react/experimental'
import {AlertIcon} from '@primer/octicons-react'
import {Icon} from '@github-ui/pacer/Icon'
import {useTasks} from '@github-ui/agent-sessions/hooks/useTasks'
import {useCreateTask} from '@github-ui/agent-sessions/hooks/useCreateTask'
import {getLeadingVisualIconAttributes} from '@github-ui/agent-sessions/components/TaskListItem'
import {ConsecutiveCAPI401sProvider} from '@github-ui/agent-sessions/contexts/ConsecutiveCAPI401sContext'
import type {OptimisticTask, UITask} from '@github-ui/agent-sessions/types/task'
import {NewAgentTaskForm} from '@github-ui/new-agent-task-form/NewAgentTaskForm'
import {useCopilotChatPayload} from './hooks/useCopilotChatPayload'
import {useCodingAgentAccessPayload} from './hooks/useCodingAgentAccessPayload'
import {memo, useCallback, useMemo} from 'react'
import type {SessionState} from '@github-ui/agent-sessions/types/session'

export function GlobalCopilotOverlay({onClose}: {onClose?: () => void}) {
  return (
    <ConsecutiveCAPI401sProvider>
      <GlobalCopilotOverlayContent onClose={onClose} />
    </ConsecutiveCAPI401sProvider>
  )
}

function GlobalCopilotOverlayContent({onClose}: {onClose?: () => void}) {
  // Once this PR (https://github.com/github/github/pull/392100) merges, we'll be able to remove this as it's duplicative of what's already in
  // NewAgentTaskForm. We just need it here in this component now to pass an apiURL into useTasks below, but we should move to just using getEnv().copilotApiOverrideUrl
  // inside useTasks in the future.
  const copilotChatPayload = useCopilotChatPayload()
  const missionControlOverlayPayload = useCodingAgentAccessPayload()
  const hasCCAAccess = !!missionControlOverlayPayload?.hasCCAAccess
  const {tasks, isLoading, isError} = useTasks({apiURL: copilotChatPayload?.apiURL})
  const isTasksLoading = isLoading || !missionControlOverlayPayload || !copilotChatPayload

  const {createTask} = useCreateTask({apiURL: copilotChatPayload?.apiURL})

  // Memoize the link click handler to prevent unnecessary re-renders
  const handleLinkClick = useCallback(
    (event: React.MouseEvent) => {
      // Only close if it's not a cmd+click (metaKey on Mac) or ctrl+click (ctrlKey on Windows/Linux)
      // These are used to open links in new tabs
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      const isModifiedClick = event.metaKey || event.ctrlKey
      if (!isModifiedClick && onClose) {
        onClose()
      }
    },
    [onClose],
  )

  return (
    <div className={styles.overlay}>
      <div className={styles.header}>
        <h2 className="f5">Agent tasks</h2>
      </div>
      {/* TODO: Add conditional shadows on top and bottom when content is hidden by overflow in either direction. */}
      <div className={styles.content}>
        {isTasksLoading && tasks.length === 0 ? (
          <TasksLoadingState />
        ) : !hasCCAAccess ? (
          <NoAccess canModifyCopilotSettings={!!missionControlOverlayPayload?.canModifyCopilotSettings} />
        ) : (
          <>
            <NewAgentTaskForm createTask={createTask} eventType="agents_panel" showHeader={false} />
            {isError ? (
              <TasksErrorState />
            ) : tasks.length === 0 ? (
              <TasksEmptyState />
            ) : (
              <TasksList tasks={tasks} onLinkClick={handleLinkClick} />
            )}
          </>
        )}
      </div>
    </div>
  )
}

function NoAccess({canModifyCopilotSettings}: {canModifyCopilotSettings: boolean}) {
  const headingText = "You don't have access to GitHub Copilot coding agent"
  if (canModifyCopilotSettings) {
    return (
      <Blankslate narrow>
        <Blankslate.Heading>{headingText}</Blankslate.Heading>
        <Blankslate.Description>
          Copilot coding agent is not available in the free plan. Upgrade to Pro to let Copilot tackle your TODOs.
        </Blankslate.Description>
        <Button className="mt-3" as="a" href="https://github.com/settings/copilot/features">
          Upgrade now
        </Button>
      </Blankslate>
    )
  } else {
    return (
      <Blankslate narrow>
        <Blankslate.Heading>{headingText}</Blankslate.Heading>
        <Blankslate.Description>
          Copilot coding agent is not available because the Copilot coding agent policy has been disabled by an
          administrator.
        </Blankslate.Description>
        <Blankslate.SecondaryAction href="https://docs.github.com/copilot/concepts/coding-agent/coding-agent">
          Learn more
        </Blankslate.SecondaryAction>
      </Blankslate>
    )
  }
}

// There's an issue with ActionList.LinkItem/Item being unable to handle icon transitions properly unless we memoize the component
const TaskItem = memo(function TaskItem({
  state,
  updatedAt,
  prTitle,
  prState,
  prUrl,
  prNwo,
  prInMergeQueue,
  onLinkClick,
}: {
  state: SessionState
  updatedAt: string
  prTitle: string
  prState: string
  prUrl: string
  prNwo: string
  prInMergeQueue: boolean
  onLinkClick: (event: React.MouseEvent) => void
}) {
  const iconAttributes = useMemo(
    () => getLeadingVisualIconAttributes(prState, prInMergeQueue ?? false, state),
    [prState, prInMergeQueue, state],
  )

  return (
    <ActionList.LinkItem className={styles.task} href={prUrl} onClick={onLinkClick}>
      <ActionList.LeadingVisual>
        <Icon icon={iconAttributes.icon} color={iconAttributes.color} />
      </ActionList.LeadingVisual>
      {prTitle}
      <ActionList.Description variant="block" className="no-wrap">
        {prNwo} · <RelativeTime datetime={updatedAt} format="micro" className={styles.taskStartDate} />
      </ActionList.Description>
    </ActionList.LinkItem>
  )
})

function TasksList({tasks, onLinkClick}: {tasks: UITask[]; onLinkClick: (event: React.MouseEvent) => void}) {
  return (
    <ActionList showDividers className={styles.tasksList}>
      <ActionList.Group>
        <ActionList.GroupHeading as="h3" className={styles.tasksListHeading}>
          Recent tasks{' '}
          {tasks && tasks.length !== 0 && (
            <Link href="/copilot/agents" className={styles.viewAllLink}>
              View all
            </Link>
          )}
        </ActionList.GroupHeading>
        {tasks.map(task => {
          if (task.isOptimistic) {
            return <OptimisticTaskItem key={task.latest_session_updated_at} task={task as OptimisticTask} />
          } else if (task.pull) {
            return (
              <TaskItem
                key={task.pull.id}
                state={task.latest_session_state}
                updatedAt={task.latest_session_updated_at}
                prTitle={task.pull.title}
                prState={task.pull.state}
                prUrl={task.pull.url}
                prNwo={task.pull.repository_nwo}
                prInMergeQueue={task.pull.inMergeQueue ?? false}
                onLinkClick={onLinkClick}
              />
            )
          }
        })}
      </ActionList.Group>
    </ActionList>
  )
}

function OptimisticTaskItem({task}: {task: OptimisticTask}) {
  return (
    <ActionList.Item className={styles.task} aria-label={`Creating task in ${task.nwo}: ${task.problemStatement}`}>
      <ActionList.LeadingVisual>
        <SkeletonAvatar size={16} />
      </ActionList.LeadingVisual>
      {/* eslint-disable-next-line primer-react/no-system-props */}
      <SkeletonText maxWidth="75%" />
      <ActionList.Description variant="block" className="no-wrap">
        {/* eslint-disable-next-line primer-react/no-system-props */}
        <SkeletonText size="bodySmall" maxWidth="50%" />
      </ActionList.Description>
    </ActionList.Item>
  )
}

function LoadingTaskItem() {
  return (
    <ActionList.Item disabled>
      <ActionList.LeadingVisual>
        <SkeletonAvatar size={16} />
      </ActionList.LeadingVisual>
      {/* eslint-disable-next-line primer-react/no-system-props */}
      <SkeletonText maxWidth="75%" />
      <ActionList.Description variant="block">
        {/* eslint-disable-next-line primer-react/no-system-props */}
        <SkeletonText size="bodySmall" maxWidth="50%" />
      </ActionList.Description>
    </ActionList.Item>
  )
}

function TasksLoadingState() {
  return (
    <ActionList showDividers>
      {Array.from({length: 3}, (_, index) => (
        <LoadingTaskItem key={index} />
      ))}
    </ActionList>
  )
}

function TasksEmptyState() {
  return (
    <MessageState
      heading="Start a new task with Copilot"
      description="Describe your task in natural language. Copilot will work in the background and open a pull request for your review."
      linkHref="https://docs.github.com/copilot/concepts/coding-agent/coding-agent"
      linkText="Learn more about Copilot coding agent"
    />
  )
}

function TasksErrorState() {
  return (
    <MessageState
      showAlertIcon
      heading="Tasks couldn’t be loaded"
      description="Try again or, if the problem persists, contact support."
      linkHref="https://githubstatus.com/"
      linkText="GitHub status"
    />
  )
}

function MessageState({
  showAlertIcon = false,
  heading,
  description,
  linkHref,
  linkText,
}: {
  showAlertIcon?: boolean
  heading: string
  description: string
  linkHref?: string
  linkText?: string
}) {
  return (
    <div className={styles.messageState}>
      <h3 className={styles.messageStateHeading}>
        {showAlertIcon && <AlertIcon className="var(--fgColor-attention)" />}
        {heading}
      </h3>
      <p className={styles.messageStateDescription}>{description}</p>
      {linkHref && linkText && (
        <Link href={linkHref} inline className="d-inline-block">
          {linkText}
        </Link>
      )}
    </div>
  )
}

try{ GlobalCopilotOverlay.displayName ||= 'GlobalCopilotOverlay' } catch {}
try{ GlobalCopilotOverlayContent.displayName ||= 'GlobalCopilotOverlayContent' } catch {}
try{ NoAccess.displayName ||= 'NoAccess' } catch {}
try{ TaskItem.displayName ||= 'TaskItem' } catch {}
try{ TasksList.displayName ||= 'TasksList' } catch {}
try{ OptimisticTaskItem.displayName ||= 'OptimisticTaskItem' } catch {}
try{ LoadingTaskItem.displayName ||= 'LoadingTaskItem' } catch {}
try{ TasksLoadingState.displayName ||= 'TasksLoadingState' } catch {}
try{ TasksEmptyState.displayName ||= 'TasksEmptyState' } catch {}
try{ TasksErrorState.displayName ||= 'TasksErrorState' } catch {}
try{ MessageState.displayName ||= 'MessageState' } catch {}